import type { Answers, FlowIntent, I18n, Language, LeadFormValues } from './types'

const L = (es: string, en: string): I18n => ({ es, en })

const PROVINCIA: Record<string, I18n> = {
  valencia: L('Valencia', 'Valencia'),
  castellon: L('Castellón', 'Castellón'),
  alicante: L('Alicante', 'Alicante'),
}

const TIPO_PROPIEDAD: Record<string, I18n> = {
  piso: L('piso', 'apartment'),
  chalet: L('chalet', 'house/chalet'),
  local: L('local comercial', 'commercial space'),
  garaje: L('garaje', 'garage'),
  otro: L('otra propiedad', 'other type of property'),
}

const PROPERTY_TYPE_OWNER: Record<string, I18n> = {
  piso: L('piso', 'apartment'),
  chalet: L('chalet', 'house/chalet'),
  local: L('local comercial', 'commercial space'),
  garaje: L('garaje', 'garage'),
  otro: L('otra propiedad', 'other type of property'),
}

const PRESUPUESTO_COMPRA: Record<string, I18n> = {
  lt_200000: L('menos de 200.000 €', 'less than €200,000'),
  '200000_400000': L('entre 200.000 € y 400.000 €', 'between €200,000 and €400,000'),
  '400000_600000': L('entre 400.000 € y 600.000 €', 'between €400,000 and €600,000'),
  '600000_800000': L('entre 600.000 € y 800.000 €', 'between €600,000 and €800,000'),
  gt_800000: L('más de 800.000 €', 'more than €800,000'),
}

const PRESUPUESTO_ALQUILER: Record<string, I18n> = {
  lt_1000: L('menos de 1.000 €/mes', 'less than €1,000/month'),
  '1000_1500': L('entre 1.000 € y 1.500 €/mes', 'between €1,000 and €1,500/month'),
  '1500_2000': L('entre 1.500 € y 2.000 €/mes', 'between €1,500 and €2,000/month'),
  '2000_4000': L('entre 2.000 € y 4.000 €/mes', 'between €2,000 and €4,000/month'),
  gt_4000: L('más de 4.000 €/mes', 'more than €4,000/month'),
}

const ESTADO: Record<string, I18n> = {
  nuevo: L('a estrenar', 'brand new'),
  reformado: L('reformado', 'recently renovated'),
  a_reformar: L('a reformar', 'needs renovation'),
}

const INTENTION: Record<string, I18n> = {
  vender: L('vender', 'sell'),
  alquilar: L('alquilar', 'rent out'),
}

function label(map: Record<string, I18n>, key: unknown, lang: Language): string | null {
  if (typeof key !== 'string') return null
  const entry = map[key]
  return entry ? entry[lang] : key
}

function str(value: unknown): string | null {
  if (value === null || value === undefined) return null
  const s = String(value).trim()
  return s.length > 0 ? s : null
}

export function buildMessage(
  intent: FlowIntent,
  answers: Answers,
  language: Language
): string {
  switch (intent) {
    case 'comprar': {
      const ref = str(answers.ref_code)
      if (ref) {
        return language === 'es'
          ? `Interesado en la propiedad con referencia ${ref}.`
          : `Interested in the property with reference ${ref}.`
      }
      const provincia = label(PROVINCIA, answers.provincia, language)
      const tipo = label(TIPO_PROPIEDAD, answers.tipo_propiedad, language)
      const presupuesto = label(PRESUPUESTO_COMPRA, answers.presupuesto, language)
      if (language === 'es') {
        return `Busco comprar un ${tipo ?? 'inmueble'} en ${provincia ?? 'la zona indicada'}. Presupuesto aproximado: ${presupuesto ?? 'no especificado'}.`
      }
      return `Looking to buy ${tipo ?? 'a property'} in ${provincia ?? 'the selected area'}. Approximate budget: ${presupuesto ?? 'not specified'}.`
    }

    case 'alquilar': {
      const provincia = label(PROVINCIA, answers.provincia, language)
      const tipo = label(TIPO_PROPIEDAD, answers.tipo_propiedad, language)
      const presupuesto = label(PRESUPUESTO_ALQUILER, answers.presupuesto_alquiler, language)
      if (language === 'es') {
        return `Busco alquilar un ${tipo ?? 'inmueble'} en ${provincia ?? 'la zona indicada'}. Presupuesto mensual: ${presupuesto ?? 'no especificado'}.`
      }
      return `Looking to rent ${tipo ?? 'a property'} in ${provincia ?? 'the selected area'}. Monthly budget: ${presupuesto ?? 'not specified'}.`
    }

    case 'propietario': {
      const intention = label(INTENTION, answers.intention, language)
      const tipo = label(PROPERTY_TYPE_OWNER, answers.property_type, language)
      const location = str(answers.property_location)
      const sqmts = str(answers.property_sqmts)
      const estado = label(ESTADO, answers.estado, language)
      if (language === 'es') {
        const parts: string[] = []
        parts.push(`Quiero ${intention ?? 'gestionar'} mi ${tipo ?? 'propiedad'}`)
        if (location) parts.push(`en ${location}`)
        const tail: string[] = []
        if (sqmts) tail.push(`${sqmts} m²`)
        if (estado) tail.push(`estado: ${estado}`)
        return tail.length > 0 ? `${parts.join(' ')}, ${tail.join(', ')}.` : `${parts.join(' ')}.`
      }
      const parts: string[] = []
      parts.push(`I want to ${intention ?? 'manage'} my ${tipo ?? 'property'}`)
      if (location) parts.push(`in ${location}`)
      const tail: string[] = []
      if (sqmts) tail.push(`${sqmts} m²`)
      if (estado) tail.push(`condition: ${estado}`)
      return tail.length > 0 ? `${parts.join(' ')}, ${tail.join(', ')}.` : `${parts.join(' ')}.`
    }

    case 'agente':
      return language === 'es'
        ? 'Solicita hablar con un agente.'
        : 'Requesting to speak with an agent.'

    case 'admin': {
      const consulta = str(answers.consulta)
      if (consulta) return consulta
      return language === 'es' ? 'Consulta administrativa.' : 'Administrative inquiry.'
    }

    case 'publicidad': {
      const idea = str(answers.idea)
      if (idea) {
        return language === 'es'
          ? `Propuesta de publicidad/colaboración: ${idea}`
          : `Advertising / partnership proposal: ${idea}`
      }
      return language === 'es'
        ? 'Propuesta de publicidad o colaboración.'
        : 'Advertising or partnership proposal.'
    }
  }
}

export function buildCharacteristics(answers: Answers, language: Language): string[] | undefined {
  const estadoLabel = label(ESTADO, answers.estado, language)
  if (estadoLabel) return [estadoLabel]
  return undefined
}

export function getLeadValues(answers: Answers): LeadFormValues {
  return {
    name: String(answers.lead_name ?? '').trim(),
    email: String(answers.lead_email ?? '').trim(),
    phone: String(answers.lead_phone ?? '').trim(),
    rgpd: answers.lead_rgpd === true,
  }
}
